import React from "react";
const roundIfDivisionError = (value) => {
    const decimal = value % 1;
    const divisionErrorLowerThreshold = 0.00001;
    const divisionErrorUpperThreshold = 0.99999;
    const isInLowerThreshold = decimal < divisionErrorLowerThreshold;
    const isInUpperThreshold = decimal > divisionErrorUpperThreshold;
    const isInThreshold = isInLowerThreshold || isInUpperThreshold;
    return isInThreshold ? Math.round(value) : value;
};
const applyStepToFraction = (fraction, min, max, step) => {
    const difference = max - min;
    const n = difference / step;
    const lower = (1 / n) * Math.floor(n * fraction);
    const upper = (1 / n) * Math.ceil(n * fraction);
    const differenceToLower = fraction - lower;
    const differentToUpper = upper - fraction;
    return differenceToLower < differentToUpper ? lower : upper;
};
const convertFractionToExternalValue = (fraction, min, max) => {
    const difference = max - min;
    return fraction * difference + min;
};
const convertExternalValueToFraction = (externalValue, min, max) => {
    if (!externalValue || isNaN(externalValue)) {
        return 0;
    }
    const difference = max - min;
    return (externalValue - min) / difference;
};
class Slider extends React.Component {
    constructor(props) {
        super(props);
        this.mouseMoveEventListener = this.mouseMoveEventListener.bind(this);
        this.mouseUpEventListener = this.mouseUpEventListener.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleSliderPress = this.handleSliderPress.bind(this);
        this.handleSliderRelease = this.handleSliderRelease.bind(this);
        this.handleSliderMove = this.handleSliderMove.bind(this);
        this.calculateNewFractionPosition = this.calculateNewFractionPosition.bind(this);
        this.getValueInsideOfBounds = this.getValueInsideOfBounds.bind(this);
        this.getFractionPosition = this.getFractionPosition.bind(this);
        this.getPercentagePositionFromFraction = this.getPercentagePositionFromFraction.bind(this);
        this.sliderRef = React.createRef();
        this.state = {
            isDragging: false,
            value: 0,
        };
    }
    static getDerivedStateFromProps(props, state) {
        const value = convertExternalValueToFraction(props.initialValue, props.min, props.max) || 0;
        if (value !== state.value) {
            return {
                ...state,
                value,
            };
        }
        return null;
    }
    componentDidMount() {
        window.addEventListener("mouseup", this.mouseUpEventListener);
        window.addEventListener("mousemove", this.mouseMoveEventListener);
    }
    componentWillUnmount() {
        window.removeEventListener("mouseup", this.mouseUpEventListener);
        window.removeEventListener("mousemove", this.mouseMoveEventListener);
    }
    mouseMoveEventListener(e) {
        this.handleSliderMove(e.clientX);
    }
    mouseUpEventListener(e) {
        if (this.state.isDragging) {
            e.preventDefault();
            e.stopPropagation();
            this.handleSliderRelease();
            return false;
        }
        return true;
    }
    handleTouchMove(e) {
        this.handleSliderMove(e.touches[0].pageX);
    }
    handleSliderPress() {
        if (this.props.isDisabled) {
            return;
        }
        this.setState({
            isDragging: true,
        });
    }
    handleSliderRelease() {
        this.setState({
            isDragging: false,
        }, () => this.props.onAfterChange());
    }
    handleSliderMove(x) {
        if (!this.state.isDragging) {
            return;
        }
        const fraction = this.calculateNewFractionPosition(x);
        const fractionInStep = applyStepToFraction(fraction, this.props.min, this.props.max, this.props.step);
        const externalValueInStep = convertFractionToExternalValue(fractionInStep, this.props.min, this.props.max);
        const externalValueRounded = roundIfDivisionError(externalValueInStep);
        this.setState({
            value: fraction,
        }, () => {
            this.props.onChange(externalValueRounded);
        });
    }
    calculateNewFractionPosition(x) {
        const boundaryMin = this.sliderRef.current.getBoundingClientRect().left;
        const boundaryMax = this.sliderRef.current.getBoundingClientRect()
            .right;
        const valueInsideOfBounds = this.getValueInsideOfBounds(x, boundaryMin, boundaryMax);
        return this.getFractionPosition(valueInsideOfBounds, boundaryMin, boundaryMax);
    }
    getValueInsideOfBounds(value, min, max) {
        const isLessThanMin = value < min;
        const isMoreThanMax = value > max;
        if (isLessThanMin) {
            return min;
        }
        if (isMoreThanMax) {
            return max;
        }
        return value;
    }
    getFractionPosition(value, min, max) {
        const newValue = value - min;
        const newMax = max - min;
        return newValue / newMax;
    }
    getPercentagePositionFromFraction(fraction) {
        const fractionWithStep = applyStepToFraction(fraction, this.props.min, this.props.max, this.props.step);
        return fractionWithStep * 100;
    }
    render() {
        const handlePercentagePosition = this.getPercentagePositionFromFraction(this.state.value);
        const trackStyle = {
            visibility: "visible",
            left: "0%",
            width: `${handlePercentagePosition}%`,
        };
        const handleStyle = {
            left: `${handlePercentagePosition}%`,
        };
        return (React.createElement("div", { "data-ecom-rangeslider": "", className: this.props.isDisabled ? "is-disabled" : "", ref: this.sliderRef },
            React.createElement("div", { className: "range-slider" },
                React.createElement("div", { className: "range-slider-bar" },
                    React.createElement("div", { className: "range-slider-rail" }),
                    React.createElement("div", { className: "range-slider-track", style: trackStyle }),
                    React.createElement("div", { className: "range-slider-handle", style: handleStyle, onMouseDown: this.handleSliderPress, onMouseUp: this.handleSliderRelease, onTouchStart: this.handleSliderPress, onTouchEnd: this.handleSliderRelease, onTouchMove: this.handleTouchMove })))));
    }
}
export default Slider;
